import React from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { readSharedLens, adoptSharedLens } from "@/lib/phase3_api";
import { ArrowLeft, DownloadSimple, WarningCircle } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

function List({ label, values }) {
  return (
    <div className="border border-zinc-200 bg-white p-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
        {label} · {(values || []).length}
      </div>
      {(values || []).length === 0 ? (
        <div className="text-sm text-zinc-500">—</div>
      ) : (
        <ul className="space-y-1 text-sm text-zinc-800 list-disc pl-5">
          {values.map((v, i) => <li key={i}>{v}</li>)}
        </ul>
      )}
    </div>
  );
}

export default function SharedLens() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [asName, setAsName] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ["shared-lens", shareId],
    queryFn: () => readSharedLens(shareId),
    retry: false,
  });

  const lens = data?.lens;

  React.useEffect(() => {
    if (lens && !asName) setAsName(lens.vertical);
  }, [lens]); // eslint-disable-line react-hooks/exhaustive-deps

  const doAdopt = async (useAsName) => {
    try {
      setBusy(true);
      const result = await adoptSharedLens(shareId, useAsName ? asName : null);
      toast.success(`Adopted as "${result.adopted_as}"`);
      navigate("/lenses");
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      // Collision → surface as-name field
      if (detail && detail.includes("already exists")) {
        toast.warning(detail);
      } else {
        toast.error(detail || "Adopt failed");
      }
    } finally { setBusy(false); }
  };

  if (isLoading) return <div className="min-h-screen p-10 font-mono text-sm text-zinc-500">Loading shared lens…</div>;
  if (error || !lens) {
    return (
      <div className="min-h-screen p-10" data-testid="shared-lens-error">
        <div className="border border-red-300 bg-red-50 p-6 max-w-lg">
          <div className="font-display text-lg text-red-700 font-semibold flex items-center gap-2">
            <WarningCircle size={18} /> Shared lens not found
          </div>
          <div className="text-sm text-red-700 mt-1">This link may have been revoked or is invalid.</div>
          <Link to="/lenses" className="mt-3 inline-block font-mono text-xs uppercase tracking-widest text-red-700 hover:underline">
            ← Back to lenses
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="shared-lens-page">
      <header className="border-b border-zinc-200 px-6 lg:px-12 py-6">
        <Link to="/" className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-zinc-500 hover:text-zinc-900 mb-2">
          <ArrowLeft size={12} /> TeardownIQ home
        </Link>
        <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">
          Shared Industry Lens · read-only
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-zinc-950 mt-1">
          {lens.vertical}
        </h1>
      </header>

      <div className="max-w-4xl mx-auto px-6 lg:px-12 py-8 space-y-6">
        <div className="border border-zinc-950 bg-zinc-950 text-white p-6">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 mb-1">
            Adopt into your workspace
          </div>
          <div className="text-sm text-zinc-200 mb-4">
            Adopting copies this lens into your TeardownIQ workspace as a custom lens you can edit or delete.
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={asName}
              onChange={(e) => setAsName(e.target.value)}
              placeholder="Name in your workspace"
              className="sharp bg-white text-zinc-950 font-mono text-sm border-0 h-10 flex-1"
              data-testid="adopt-as-input"
            />
            <button
              onClick={() => doAdopt(true)}
              disabled={busy || !asName.trim()}
              data-testid="adopt-lens-button"
              className="bg-white text-zinc-950 px-6 h-10 font-mono text-xs uppercase tracking-widest hover:bg-zinc-100 flex items-center gap-2 disabled:opacity-50"
            >
              <DownloadSimple size={13} weight="bold" /> {busy ? "Adopting…" : "Adopt"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
          <List label="Buyer concerns" values={lens.buyer_concerns} />
          <List label="Compliance / regulatory" values={lens.compliance} />
          <List label="Expected integrations" values={lens.integration_expectations} />
          <List label="Churn drivers" values={lens.churn_drivers} />
          <List label="Anti-patterns" values={lens.anti_patterns} />
          <List label="Aliases (fuzzy match)" values={lens.aliases} />
        </div>

        {lens.security_data_sensitivity && (
          <div className="border border-zinc-200 bg-white p-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
              Security / data sensitivity
            </div>
            <div className="text-sm text-zinc-800">{lens.security_data_sensitivity}</div>
          </div>
        )}
      </div>
    </div>
  );
}
